import { ParentPushRuntime, KidPushRuntime } from './components/Push/PushRuntime';
import { diagnostic } from './utils/diagnostics';
import React, { useEffect } from 'react';
import { Capacitor } from '@capacitor/core';
import { App as CapacitorApp } from '@capacitor/app';
import { supabase } from './services/supabase';
import { readParentOAuthTransaction } from './utils/parentOAuth';
import { getNativeParentOAuthCallbackUrl } from './utils/nativeParentOAuth';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
  useNavigate,
} from 'react-router-dom';

import { AuthProvider, useAuth } from './contexts/AuthContext';
import { KidAuthProvider, useKidAuth } from './contexts/KidAuthContext';
import { ParentThemeProvider } from './contexts/ParentThemeContext';
import { FamilyStatusProvider,} from './contexts/FamilyStatusContext';

// Parent pages
import ParentLogin from './pages/parent/ParentLogin';
import ForgotPassword from './pages/parent/ForgotPassword';
import ResetPassword from './pages/parent/ResetPassword';
import GuardianInvite from './pages/parent/GuardianInvite';
import OAuthCallback from './pages/parent/OAuthCallback';
import ParentDashboard from './pages/parent/ParentDashboard';
import AddChild from './pages/parent/AddChild';
import ChoreManagement from './pages/parent/ChoreManagement';
import ManageChores from './pages/parent/ManageChores';
import CompleteSetup from './pages/parent/CompleteSetup';
import ParentTransactions from './pages/parent/ParentTransactions';
import ParentReports from './pages/parent/ParentReports';
import ManageFamily from './pages/parent/ManageFamily';
import ParentSettings from './pages/parent/ParentSettings';
import ParentOnboarding from './pages/parent/ParentOnboarding';
import EntryPortal from './pages/EntryPortal';
import AppDownload from './pages/AppDownload';
import AdminPortal from './pages/admin/AdminPortal';

// Kid pages
import KidLogin from './pages/kid/KidLogin';
import KidDashboard from './pages/kid/KidDashboard';
import AddIncome from './pages/kid/AddIncome';
import AddExpense from './pages/kid/AddExpense';
import Savings from './pages/kid/Savings';
import KidChores from './pages/kid/KidChores';
import KidAddChore from './pages/kid/KidAddChore';
import KidReports from './pages/kid/KidReports';

import './App.css';

// --------------------
// Parent Protected Routes
// --------------------
export const ProtectedParentRoute = ({ children }) => {
  const {
    user,
    parent,
    initializing,
    parentChecked,
    parentLoadError,
    refreshParent,
    signOut,
  } = useAuth();

  const location = useLocation();

  if (initializing) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        Loading...
      </div>
    );
  }

  if (!user) {
    return (
      <Navigate
        to="/parent/login"
        replace
        state={{ from: location }}
      />
    );
  }

  if (!parentChecked) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        Loading your profile...
      </div>
    );
  }

  if (parentLoadError) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-3 p-6 text-center">
        <div className="text-lg font-semibold">
          We couldn’t load your family profile.
        </div>

        <div className="text-sm text-gray-600 max-w-md">
          This is usually a temporary network issue. You can
          retry, or sign out and back in.
        </div>

        <div className="flex gap-3">
          <button
            className="px-4 py-2 rounded bg-blue-600 text-white hover:bg-blue-500"
            onClick={() =>
              refreshParent({
                blockUI: true
              })
            }
          >
            Retry
          </button>

          <button
            className="px-4 py-2 rounded bg-gray-200 hover:bg-gray-300"
            onClick={async () => {
              try {
                await signOut();
              } finally {
                window.location.href = '/parent/login';
              }
            }}
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  if (!parent) {
    return (
      <Navigate
        to="/parent/setup"
        replace
        state={{ from: location }}
      />
    );
  }

  const isOwnerOnboarding =
    parent.role === 'owner' &&
    ![
      'completed',
      'skipped',
    ].includes(
      parent.onboarding_status
    );

  const isOnboardingRoute =
    location.pathname ===
      '/parent/onboarding';

  const isParentDashboardRoute =
    location.pathname === '/parent' ||
    location.pathname === '/parent/';

  const isOwnerDashboardTour =
    isOwnerOnboarding &&
    parent.onboarding_status ===
      'in_progress' &&
    [
      'parent_settings',
      'pocket_tip',
      'transactions',
      'chores',
      'reports',
      'manage_family',
    ].includes(
      parent.onboarding_step
    );

  if (
    isOwnerOnboarding &&
    isOwnerDashboardTour &&
    !isParentDashboardRoute
  ) {
    return (
      <Navigate
        to="/parent"
        replace
        state={{
          dashboardTour: 'initial',
        }}
      />
    );
  }

  if (
    isOwnerOnboarding &&
    !isOwnerDashboardTour &&
    !isOnboardingRoute
  ) {
    return (
      <Navigate
        to="/parent/onboarding"
        replace
      />
    );
  }

  if (
    !isOwnerOnboarding &&
    isOnboardingRoute
  ) {
    return (
      <Navigate
        to="/parent"
        replace
      />
    );
  }

  return children;
};

// --------------------
// Setup-only protected route
// --------------------
export const ProtectedSetupRoute = ({ children }) => {
  const {
    user,
    parent,
    initializing,
    parentChecked
  } = useAuth();

  const location = useLocation();

  if (initializing) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        Loading...
      </div>
    );
  }

  if (!user) {
    return (
      <Navigate
        to="/parent/login"
        replace
        state={{ from: location }}
      />
    );
  }

  if (!parentChecked) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        Loading...
      </div>
    );
  }

  if (parent) {
    return (
      <Navigate
        to="/parent"
        replace
      />
    );
  }

  return children;
};

// --------------------
// Kid Protected Route
// --------------------
const ProtectedKidRoute = ({ children }) => {
  const {
    kidUser,
    currentKid,
    loading,
    kidLoading,
    kidChecked,
    kidLoadError,
    refreshKid,
    logoutKid,
  } = useKidAuth();

  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        Loading...
      </div>
    );
  }

  if (!kidUser) {
    return (
      <Navigate
        to="/kid/login"
        replace
        state={{ from: location }}
      />
    );
  }

  if (
    kidLoading ||
    !kidChecked
  ) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        Loading your profile...
      </div>
    );
  }

  if (
    kidLoadError ||
    !currentKid
  ) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-3 p-6 text-center">
        <div className="text-lg font-semibold">
          We couldn’t load your kid profile.
        </div>

        <div className="text-sm text-gray-600 max-w-md">
          This may be a temporary connection issue.
          Try again, or return to Kid Login.
        </div>

        <div className="flex gap-3">
          <button
            className="px-4 py-2 rounded bg-purple-600 text-white hover:bg-purple-500"
            onClick={() =>
              refreshKid({
                blockUI: true
              })
            }
          >
            Retry
          </button>

          <button
            className="px-4 py-2 rounded bg-gray-200 hover:bg-gray-300"
            onClick={async () => {
              await logoutKid();
            }}
          >
            Kid Login
          </button>
        </div>
      </div>
    );
  }

  return children;
};

// --------------------
// Kid route branch
// --------------------
const KidRoutes = () => {
  return (
    <KidAuthProvider>
      <KidPushRuntime />
      <Routes>
        {/* /kid */}
        <Route
          path="/"
          element={
            <Navigate
              to="/kid/login"
              replace
            />
          }
        />

        {/* /kid/login */}
        <Route
          path="login"
          element={<KidLogin />}
        />

        {/* /kid/dashboard */}
        <Route
          path="dashboard"
          element={
            <ProtectedKidRoute>
              <KidDashboard />
            </ProtectedKidRoute>
          }
        />

        {/* /kid/add-income */}
        <Route
          path="add-income"
          element={
            <ProtectedKidRoute>
              <AddIncome />
            </ProtectedKidRoute>
          }
        />

        {/* /kid/add-expense */}
        <Route
          path="add-expense"
          element={
            <ProtectedKidRoute>
              <AddExpense />
            </ProtectedKidRoute>
          }
        />

        {/* /kid/savings */}
        <Route
          path="savings"
          element={
            <ProtectedKidRoute>
              <Savings />
            </ProtectedKidRoute>
          }
        />

        {/* /kid/chores */}
        <Route
          path="chores"
          element={
            <ProtectedKidRoute>
              <KidChores />
            </ProtectedKidRoute>
          }
        />

        {/* /kid/chores/new */}
        <Route
          path="chores/new"
          element={
            <ProtectedKidRoute>
              <KidAddChore />
            </ProtectedKidRoute>
          }
        />

        {/* /kid/reports */}
        <Route
          path="reports"
          element={
            <ProtectedKidRoute>
              <KidReports />
            </ProtectedKidRoute>
          }
        />

        {/* fallback within /kid */}
        <Route
          path="*"
          element={
            <Navigate
              to="/kid/login"
              replace
            />
          }
        />
      </Routes>
    </KidAuthProvider>
  );
};

// --------------------
// Parent route branch
// --------------------
const ParentRoutes = () => {
  return (
    <Routes>
      {/* /parent */}
      <Route
        path="/"
        element={<ParentDashboard />}
      />

      {/* Shared child list and Manage Child detail routes. */}
      <Route path="children" element={<ParentDashboard />} />
      <Route path="children/:childId" element={<ParentDashboard />} />

      {/* /parent/onboarding */}
      <Route
        path="onboarding"
        element={<ParentOnboarding />}
      />

      {/* /parent/add-child */}
      <Route
        path="add-child"
        element={<AddChild />}
      />

      {/* /parent/chores/manage */}
      <Route
        path="chores/manage"
        element={<ManageChores />}
      />

      {/* /parent/chores/review */}
      <Route
        path="chores/review"
        element={<ChoreManagement />}
      />

      {/* /parent/transactions */}
      <Route
        path="transactions"
        element={<ParentTransactions />}
      />

      {/* /parent/reports */}
      <Route
        path="reports"
        element={<ParentReports />}
      />

      {/* /parent/manage-family */}
      <Route
        path="manage-family"
        element={<ManageFamily />}
      />

      {/* /parent/settings */}
      <Route
        path="settings"
        element={<ParentSettings />}
      />

      {/* fallback within /parent */}
      <Route
        path="*"
        element={
          <Navigate
            to="/parent"
            replace
          />
        }
      />
    </Routes>
  );
};

// Complete native PKCE in the WebView that initiated the request.
const NativeOAuthReturn = () => {
  const navigate = useNavigate();

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return undefined;

    let active = true;
    let lastUrl = null;

    const handleUrl = async (url) => {
      if (!url?.startsWith(getNativeParentOAuthCallbackUrl())) return;
      if (url === lastUrl) return;
      lastUrl = url;

      const callback = new URL(url);
      const expected = new URL(getNativeParentOAuthCallbackUrl());
      if (callback.protocol !== expected.protocol ||
          callback.host !== expected.host ||
          callback.pathname !== expected.pathname) return;

      if (!readParentOAuthTransaction()) return;

      const error = callback.searchParams.get('error');
      const code = callback.searchParams.get('code');
      if (error) {
        if (active) navigate(`/auth/callback${callback.search}`, { replace: true });
        return;
      }
      if (!code) return;

      const { error: exchangeError } =
        await supabase.auth.exchangeCodeForSession(code);
      if (!active) return;

      if (exchangeError) {
        diagnostic('error', 'parent.oauth.exchange_failed');
        navigate('/auth/callback?error=session_exchange_failed', { replace: true });
        return;
      }

      navigate('/auth/callback', { replace: true });
    };

    let listener;
    CapacitorApp.addListener('appUrlOpen', ({ url }) => handleUrl(url))
      .then((handle) => { if (active) listener = handle; else handle.remove(); });
    CapacitorApp.getLaunchUrl().then((result) => {
      if (active && result?.url) handleUrl(result.url);
    });

    return () => {
      active = false;
      listener?.remove();
    };
  }, [navigate]);

  return null;
};

// --------------------
// App
// --------------------
function App() {
  // The Kids bundle has no parent or administrator routes or auth providers.
  // The web build and Parents Android bundle continue through the shared app below.
  if (Capacitor.isNativePlatform() && import.meta.env.VITE_APP_AUDIENCE === 'kids') {
    return (
      <Router>
        <Routes>
          <Route path="/kid/*" element={<KidRoutes />} />
          <Route path="*" element={<Navigate to="/kid" replace />} />
        </Routes>
      </Router>
    );
  }

  return (
    <AuthProvider>
      <FamilyStatusProvider>
      <ParentThemeProvider>
        <Router>
        <NativeOAuthReturn />
        <ParentPushRuntime />
        <Routes>
          {/* Shared MyPiggy entry portal */}
          <Route
            path="/"
            element={Capacitor.isNativePlatform() ? <Navigate to="/parent" replace /> : <EntryPortal />}
          />

          {/* Public download cards and stable store links; no login required. */}
          <Route path="/download/:audience" element={Capacitor.isNativePlatform() ? <Navigate to="/parent" replace /> : <AppDownload />} />
          <Route path="/download/:audience/:platform" element={Capacitor.isNativePlatform() ? <Navigate to="/parent" replace /> : <AppDownload />} />

          {/* Administration (intentionally absent from public navigation) */}
          <Route
            path="/admportal/*"
            element={Capacitor.isNativePlatform() ? <Navigate to="/parent" replace /> : <AdminPortal />}
          />

          {/* Parent Authentication */}
          <Route
            path="/parent/login"
            element={<ParentLogin />}
          />

          <Route
            path="/forgot-password"
            element={<ForgotPassword />}
          />

          <Route
            path="/reset-password"
            element={<ResetPassword />}
          />

          <Route
            path="/auth/callback"
            element={<OAuthCallback />}
          />

          {/* Guardian Invitation  */}
          <Route
            path="/parent/invite"
            element={<GuardianInvite />}
          />

          {/* Parent Routes */}
          <Route
            path="/parent/*"
            element={
              <ProtectedParentRoute>
                <ParentRoutes />
              </ProtectedParentRoute>
            }
          />

          <Route
            path="/parent/setup"
            element={
              <ProtectedSetupRoute>
                <CompleteSetup />
              </ProtectedSetupRoute>
            }
          />

          {/* Kid Routes */}
          <Route
            path="/kid/*"
            element={Capacitor.isNativePlatform() ? <Navigate to="/parent" replace /> : <KidRoutes />}
          />

          {/* Fallback */}
          <Route
            path="*"
            element={
              <Navigate
                to={Capacitor.isNativePlatform() ? '/parent' : '/'}
                replace
              />
            }
          />
        </Routes>
        </Router>
      </ParentThemeProvider>
      </FamilyStatusProvider>
    </AuthProvider>
  );
}

export default App;


